/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/**
 * External dependencies
 */
import { useMemo, useCallback } from '@web-stories-wp/react';
/**
 * Internal dependencies
 */
import { useStory } from '../../../../app';

type Layer = {
  id: string;
  position: number;
  isFirstLayerAfterGroup: boolean;
  canPlaceElementsOnTopOfLayer?: boolean;
  previousElementGroupId?: string;
};

type ElementLayer = Layer & {
  isGroup: false;
  isFirstLayerAfterGroup: boolean;
  previousElementGroupId?: string;
};

type GroupLayer = Layer & {
  isGroup: true;
};

function useLayers() {
  const {
    elements,
    arrangeElement,
    arrangeGroup,
    arrangeSelection,
    groups,
    selectedElementIds,
  } = useStory(({ state, actions }) => ({
    elements: state.currentPage?.elements || [],
    arrangeElement: actions.arrangeElement,
    arrangeGroup: actions.arrangeGroup,
    arrangeSelection: actions.arrangeSelection,
    groups: state.currentPage?.groups || {},
    selectedElementIds: state.selectedElementIds,
  }));

  const elementLayers = useMemo(
    () =>
      elements.map(({ id, groupId, layerName }, position) => ({
        id,
        groupId,
        layerName,
        position,
      })),
    [elements]
  );

  elementLayers.reverse();

  // This is a list of elements and groups in the order they're displayed
  const layers = useMemo(() => {
    const groupIds = new Set();

    const elementsAndGroups: (ElementLayer | GroupLayer)[] = [];

    let lastElementPosition = 0;

    for (
      let elementIndex = 0;
      elementIndex < elementLayers.length;
      elementIndex++
    ) {
      const element = elementLayers[elementIndex];
      const elementPosition = element.position;
      const previousElementPosition = elementPosition + 1;
      const previousElement = elementLayers.find(
        (el) => el.position === previousElementPosition
      );
      const previousElementGroupId = previousElement?.groupId;

      let isFirstLayerAfterGroup = false;

      if (element.position > lastElementPosition) {
        lastElementPosition = element.position;
      }

      if (elementIndex > 0 && Boolean(previousElementGroupId)) {
        const elementHasDifferentGroupId =
          element.groupId !== previousElementGroupId;
        isFirstLayerAfterGroup = elementHasDifferentGroupId;
      }

      const elementLayer: ElementLayer = {
        isGroup: false,
        isFirstLayerAfterGroup,
        id: element.id,
        position: element.position,
      };

      const isGroup = element.groupId && !groupIds.has(element.groupId);

      if (isGroup) {
        groupIds.add(element.groupId);

        const groupLayer: GroupLayer = {
          isGroup: true,
          id: element.groupId,
          position: element.position,
          isFirstLayerAfterGroup,
          ...(isFirstLayerAfterGroup ? { previousElementGroupId } : {}),
        };

        elementsAndGroups.push(groupLayer);
        elementsAndGroups.push({
          ...elementLayer,
          isFirstLayerAfterGroup: false,
        });

        continue;
      }

      // If the element on top of this one isn't in a group, we can place elements between this one and the top one
      const canPlaceElementsOnTopOfLayer = !previousElementGroupId;

      elementsAndGroups.push({
        ...elementLayer,
        ...(isFirstLayerAfterGroup ? { previousElementGroupId } : {}),
        canPlaceElementsOnTopOfLayer,
      });
    }

    return elementsAndGroups.map((layer) => ({
      ...layer,
      canPlaceElementsOnTopOfLayer:
        layer.canPlaceElementsOnTopOfLayer ||
        layer.position === lastElementPosition,
    }));
  }, [elementLayers]);

  const handleDragPosition = useCallback(
    (oldPosObj, newPosObj, evt) => {
      const { position: oldPos } = oldPosObj;
      const { position: newPos, data } = newPosObj;
      const groupId = data?.groupId;
      const oldPosElement = elementLayers.find((e) => e.position === oldPos);

      const newGroupId = newPosObj.data?.groupId;
      const oldGroupId = oldPosObj.data?.group;
      const selectionElements =
        selectedElementIds.length > 0
          ? elements.filter(({ id }) => selectedElementIds.includes(id))
          : [];
      const selectionSpansGroups =
        oldGroupId &&
        selectionElements.length > 1 &&
        selectionElements.some((element) => element.groupId !== oldGroupId);

      if (newPos === oldPos && newGroupId === oldGroupId) {
        return;
      }

      if (!oldPosElement) {
        return;
      }

      if (selectionSpansGroups) {
        const rawPosition = newPos > oldPos ? newPos + 1 : newPos;
        const targetGroupId = oldGroupId ? false : groupId;
        arrangeSelection({
          position: rawPosition,
          groupId: targetGroupId,
        });
        return;
      }

      // We are holding the whole group.
      if (oldGroupId) {
        if (!newGroupId) {
          arrangeGroup({
            groupId: oldGroupId,
            position: newPos,
          });
        } else if (newGroupId && newGroupId !== oldGroupId) {
          const elementsInGroup = elementLayers.filter(
            (el) => el.groupId === oldGroupId
          );

          if (elementsInGroup.length === 1) {
            arrangeElement({
              elementId: oldPosElement.id,
              position: newPos,
              groupId: newGroupId,
            });
          }
        }
      } else if (
        selectedElementIds.length > 1 &&
        selectedElementIds.includes(oldPosElement.id)
      ) {
        const rawPosition = newPos > oldPos ? newPos + 1 : newPos;
        arrangeSelection({
          position: rawPosition,
          groupId,
        });
      } else {
        arrangeElement({
          elementId: oldPosElement.id,
          position: newPos,
          groupId,
        });
      }
    },
    [
      arrangeElement,
      arrangeGroup,
      arrangeSelection,
      elementLayers,
      elements,
      selectedElementIds,
    ]
  );

  const visibleLayers = layers
    ? layers.filter((layer) => {
        if (layer.isGroup) {
          return true;
        }

        const element = elements.find(({ id }) => id === layer.id);

        if (element.groupId) {
          const group = groups[element.groupId];

          return group && !group.isCollapsed;
        }

        return true;
      })
    : [];

  return { layers, handleDragPosition, visibleLayers };
}

export default useLayers;
